import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import modalStyles from '@/features/application/styles/modals.module.css'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { notificationFormSchema, type NotificationFormValues } from '../types/schemas'
import {
  LIBERTY_ROLES,
  type NotificationEditModalProps,
} from '../types'

export function NotificationEditModal({
  isOpen,
  action,
  submitting,
  onClose,
  onSubmit,
}: NotificationEditModalProps) {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<NotificationFormValues>({
    resolver: zodResolver(notificationFormSchema),
    mode: 'onChange',
    defaultValues: { notification_type: '', roles: [] },
  })

  useEffect(() => {
    if (!isOpen) return
    reset({
      notification_type: action?.notification_type ?? '',
      roles: action?.roles ?? [],
    })
  }, [isOpen, action, reset])

  const roleOptions = useMemo(
    () =>
      (Object.keys(LIBERTY_ROLES) as Array<keyof typeof LIBERTY_ROLES>).map((key) => ({
        value: key,
        label: LIBERTY_ROLES[key],
      })),
    [],
  )

  const internalSubmit = (values: NotificationFormValues) => {
    if (!action) return
    onSubmit(action.an_id, {
      notification_type: values.notification_type.trim(),
      roles: values.roles,
    })
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Edit Notification Settings"
      onClose={onClose}
      maxWidth="max-w-lg"
      bodyScrollOnly
      panelDataTestId="notification-edit-modal-panel"
      footer={
        <div
          className="flex flex-wrap items-center justify-center gap-3"
          data-testid="notification-edit-modal-footer"
        >
          <button
            type="submit"
            form="notification-edit-form"
            disabled={!isDirty || submitting || !isValid}
            className={cn(primaryEntityActionButtonClass, 'min-w-[120px]')}
            data-testid="notification-edit-submit"
          >
            {submitting ? 'Saving...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={cn(secondaryEntityActionButtonClass, 'min-w-[120px]')}
            data-testid="notification-edit-cancel"
          >
            Cancel
          </button>
        </div>
      }
    >
      <form
        id="notification-edit-form"
        onSubmit={handleSubmit(internalSubmit)}
        className="space-y-4 p-2"
        noValidate
      >
        <div>
          <label
            htmlFor="notif-type"
            className="mb-1 block text-sm font-semibold text-black"
          >
            Notification Type
          </label>
          <input
            id="notif-type"
            type="text"
            readOnly
            className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 focus:outline-none"
            {...register('notification_type')}
          />
          {errors.notification_type && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.notification_type.message}
            </p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-semibold text-black">Roles</label>
          <Controller
            name="roles"
            control={control}
            render={({ field: { value, onChange } }) => (
              <SearchableSelect
                options={roleOptions}
                value={value}
                onChange={(next) => onChange(Array.isArray(next) ? next : [next])}
                placeholder="Select Role"
                searchPlaceholder="Search roles..."
                multiple
              />
            )}
          />
          {errors.roles && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.roles.message as string}
            </p>
          )}
        </div>
      </form>
    </OnScreenModal>
  )
}
